import type { PropsWithChildren } from "react";

import { TranscriptionMessage } from "@/components/TranscriptionEmptyMessage/TranscriptionEmptyMessage";
import { TranscriptionWrongCallDuration } from "@/components/TranscriptionEmptyMessage/components/TranscriptionWrongCallDuration/TranscriptionWrongCallDuration";
import { TranscriptionLowConfidenceWithTranscript } from "@/components/TranscriptionLowConfidenceWithTranscript/TranscriptionLowConfidenceWithTranscript";
import { TranscriptionNotEnabledMessage } from "@/components/TranscriptionNotEnabledMessage/TranscriptionNotEnabledMessage";
import { PlaygroundSectionLayout } from "../PlaygroundSectionLayout";
import { PlaygroundSubSectionLayout } from "../PlaygroundSubSectionLayout";
import { PlaygroundSubSectionTitle } from "../PlaygroundSubSectionTitle";

function Stage({ children }: PropsWithChildren) {
  return (
    <div className="flex h-96 rounded-md border border-border bg-card p-4">
      {children}
    </div>
  );
}

export function PlaygroundTranscriptionEmptyStates() {
  return (
    <PlaygroundSectionLayout>
      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>Bad acoustics</PlaygroundSubSectionTitle>
        <p className="text-sm font-normal text-muted-foreground">
          Shown when the audio quality was too low to transcribe. Includes a
          learn-more Anchor link.
        </p>
        <Stage>
          <TranscriptionMessage status="badAcoustics" />
        </Stage>
      </PlaygroundSubSectionLayout>

      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>
          No speech detected
        </PlaygroundSubSectionTitle>
        <p className="text-sm font-normal text-muted-foreground">
          Shown when no speech was detected on the call. Includes a learn-more
          Anchor link.
        </p>
        <Stage>
          <TranscriptionMessage status="noSpeechDetected" />
        </Stage>
      </PlaygroundSubSectionLayout>

      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>Low confidence</PlaygroundSubSectionTitle>
        <p className="text-sm font-normal text-muted-foreground">
          Shown when transcription confidence is too low, without a CTA.
        </p>
        <Stage>
          <TranscriptionMessage status="lowConfidence" />
        </Stage>
      </PlaygroundSubSectionLayout>

      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>
          Low confidence with transcript CTA
        </PlaygroundSubSectionTitle>
        <p className="text-sm font-normal text-muted-foreground">
          Same low-confidence message with a DS Button (size lg) to reveal the
          transcript anyway.
        </p>
        <Stage>
          <TranscriptionLowConfidenceWithTranscript
            onClick={() => window.alert("show transcript")}
          />
        </Stage>
      </PlaygroundSubSectionLayout>

      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>
          Wrong call duration
        </PlaygroundSubSectionTitle>
        <p className="text-sm font-normal text-muted-foreground">
          Shown when the call was too short/long. The description embeds an
          Anchor inside a Trans component.
        </p>
        <Stage>
          <TranscriptionWrongCallDuration />
        </Stage>
      </PlaygroundSubSectionLayout>

      <PlaygroundSubSectionLayout>
        <PlaygroundSubSectionTitle>
          Transcription not enabled
        </PlaygroundSubSectionTitle>
        <p className="text-sm font-normal text-muted-foreground">
          Shown when transcription is off for the line. The description and
          turn-on Button only render for users with read-line permission (live
          data driven).
        </p>
        <Stage>
          <TranscriptionNotEnabledMessage />
        </Stage>
      </PlaygroundSubSectionLayout>
    </PlaygroundSectionLayout>
  );
}
